import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";
import { TOPICS, MIN_TOPICS, MAX_TOPICS } from "@/lib/topics";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("8");
  const [timeFormat, setTimeFormat] = useState<"12h" | "24h">("24h");
  const [gmtOffset, setGmtOffset] = useState(() => {
    const offsetMin = new Date().getTimezoneOffset();
    const offsetHours = -offsetMin / 60;
    return offsetHours.toString();
  });
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const refId = searchParams.get("ref");

  useEffect(() => {
    if (refId) {
      supabase.from("referral_clicks").insert({ referrer_id: refId }).then(() => {});
    }
  }, [refId]);

  const goToTopics = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || password.length < 6) {
      toast({ title: "Check your details", description: "Email and a 6+ char password are required.", variant: "destructive" });
      return;
    }
    setStep(2);
  };

  const toggleTopic = (slug: string) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else if (next.size >= MAX_TOPICS) return prev;
      else next.add(slug);
      return next;
    });
  };

  const clearTopics = () => {
    setSelectedTopics(new Set());
  };

  const submitSignup = async (topicsOverride?: string[]) => {
    setLoading(true);
    const topics = topicsOverride ?? Array.from(selectedTopics);
    // Server enforces 0 OR 3..7. Frontend matches.
    const validTopics =
      topics.length === 0 || (topics.length >= MIN_TOPICS && topics.length <= MAX_TOPICS)
        ? topics
        : [];

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: "https://aibly.club",
        data: {
          delivery_time: parseInt(deliveryTime),
          timezone: `GMT${parseFloat(gmtOffset) >= 0 ? "+" : ""}${gmtOffset}`,
          ...(validTopics.length ? { topics: validTopics } : {}),
        },
      },
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (data.user && data.user.identities && data.user.identities.length === 0) {
      toast({
        title: "Email already registered",
        description: "This email is already in use.",
        variant: "destructive",
        action: (
          <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => navigate("/login")}>
            Login
          </Button>
        ),
      });
    } else {
      if (refId && data.user) {
        await supabase.from("referrals").insert({
          referrer_id: refId,
          referred_email: email,
          status: "pending",
        });
      }
      navigate("/confirm-email");
    }
    setLoading(false);
  };

  const noPreference = () => submitSignup([]);
  const finishWithTopics = () => submitSignup();

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const topicCount = selectedTopics.size;
  const topicsValid = topicCount === 0 || (topicCount >= MIN_TOPICS && topicCount <= MAX_TOPICS);
  const isNoPreference = topicCount === 0;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10 relative">
      <SEOHead title="Subscribe Free — aibly AI Newsletter" description="Sign up for aibly, the free daily AI newsletter. Pick the AI topics you care about." canonical="/signup" />
      <Link
        to="/"
        className="absolute top-5 left-5 flex items-center gap-1 text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span>Home</span>
      </Link>

      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-6">
            <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-16 w-auto dark:invert sm:hidden" />
            <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-10 w-auto dark:invert hidden sm:block" />
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 pb-2">
            <span className={`h-1 w-10 ${step === 1 ? "bg-accent" : "bg-border"}`} />
            <span className={`h-1 w-10 ${step === 2 ? "bg-accent" : "bg-border"}`} />
          </div>
          <p className="text-[10px] tracking-widest uppercase text-muted-foreground">
            Step {step} of 2
          </p>

          {step === 1 ? (
            <>
              <h1 className="font-display text-2xl italic text-foreground">Get the briefing. Free.</h1>
              <p className="text-xs text-muted-foreground">Choose when you want your daily AI intel delivered.</p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl italic text-foreground">Pick your AI lanes.</h1>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Choose {MIN_TOPICS}+ topics. Stories matching them float to the top of every edition. You still get every story — never a silent day.
              </p>
              <p className="text-[11px] text-muted-foreground/70">
                No favorite topics? <button type="button" onClick={noPreference} disabled={loading} className="underline hover:text-foreground">Just send me everything</button>.
              </p>
            </>
          )}
        </div>

        {step === 1 && (
          <form onSubmit={goToTopics} className="space-y-4 max-w-sm mx-auto">
            <Input
              aria-label="Email address"
              type="email"
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
            />
            <Input
              aria-label="Password"
              type="password"
              placeholder="password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
            />

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Timezone</label>
              <Select value={gmtOffset} onValueChange={setGmtOffset}>
                <SelectTrigger className="rounded-none bg-secondary border-border text-foreground text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-60">
                  {Array.from({ length: 25 }, (_, i) => i - 12).map((offset) => (
                    <SelectItem key={offset} value={offset.toString()} className="text-xs">
                      GMT{offset >= 0 ? "+" : ""}{offset}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Time Format</label>
              <div className="flex items-center gap-3">
                <Button type="button" size="sm" variant={timeFormat === "24h" ? "default" : "outline"} onClick={() => setTimeFormat("24h")} className="rounded-none text-xs">24h</Button>
                <Button type="button" size="sm" variant={timeFormat === "12h" ? "default" : "outline"} onClick={() => setTimeFormat("12h")} className="rounded-none text-xs">12h</Button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">Deliver at</label>
              <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                <SelectTrigger className="rounded-none bg-secondary border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {hours.map((h) => (
                    <SelectItem key={h} value={h.toString()}>
                      {timeFormat === "12h"
                        ? `${h === 0 ? 12 : h > 12 ? h - 12 : h}:00 ${h < 12 ? "AM" : "PM"}`
                        : `${h.toString().padStart(2, "0")}:00`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full rounded-none text-xs tracking-widest uppercase">
              Next: Pick Topics <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Already subscribed?{" "}
              <Link to="/login" className="text-foreground hover:text-accent transition-colors">Login</Link>
            </p>
          </form>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={loading}
                className="text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <div className="flex items-center gap-3">
                {topicCount > 0 && (
                  <button
                    type="button"
                    onClick={clearTopics}
                    disabled={loading}
                    className="text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                  >
                    Clear topics
                  </button>
                )}
                <span className={`text-sm font-mono ${topicsValid ? "text-foreground" : "text-muted-foreground"}`}>
                  {isNoPreference ? "No preference" : `${topicCount} / ${MAX_TOPICS}`}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={clearTopics}
              disabled={loading}
              aria-pressed={isNoPreference}
              className={`w-full text-left border px-3 py-2.5 transition-colors disabled:opacity-40 ${
                isNoPreference ? "border-accent bg-accent/10 text-foreground" : "border-border bg-background hover:border-foreground/40"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">No preference — send everything</span>
                <span className={`w-3.5 h-3.5 border ${isNoPreference ? "bg-accent border-accent" : "border-muted-foreground/40"}`} aria-hidden />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Clear selected topics and keep the full AI firehose.</p>
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TOPICS.map((t) => {
                const isOn = selectedTopics.has(t.slug);
                return (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => toggleTopic(t.slug)}
                    disabled={loading}
                    aria-pressed={isOn}
                    className={`text-left border px-3 py-2.5 transition-colors ${
                      isOn
                        ? "border-accent bg-accent/10 text-foreground"
                        : "border-border bg-background hover:border-foreground/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{t.label}</span>
                      <span
                        className={`w-3.5 h-3.5 border ${isOn ? "bg-accent border-accent" : "border-muted-foreground/40"}`}
                        aria-hidden
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{t.hint}</p>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={noPreference}
                disabled={loading}
                className="rounded-none text-xs tracking-widest uppercase"
              >
                No preference — send everything
              </Button>
              <Button
                type="button"
                onClick={finishWithTopics}
                disabled={!topicsValid || loading}
                className="rounded-none text-xs tracking-widest uppercase"
              >
                {loading ? "Creating account..." : `Subscribe — Free`}
              </Button>
            </div>
            {!topicsValid && topicCount > 0 && (
              <p className="text-center text-xs text-muted-foreground">
                Pick {MIN_TOPICS}–{MAX_TOPICS} topics, or tap "No preference" for everything.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
